import { countries, getCountryText } from '../services/helpers/country';

export interface OnboardingCountry {
    code: string;
    name: string;
}

/** Countries a store can be onboarded in from Merchant Hub. */
export const onboardingCountryCodes = ['SE', 'FI', 'NO', 'DK', 'DE', 'NL', 'EU'];

export const onboardingCountries: OnboardingCountry[] = onboardingCountryCodes.map(code => ({
    code,
    name: countries[code].name,
}));

/**
 * Countries to offer in the store onboarding flow.
 * Organizations may get per-organization country restrictions later; pass their allowed
 * country codes to narrow the list. Without a filter the full list is returned.
 */
export const getOnboardingCountries = (allowedCountryCodes?: string[]): OnboardingCountry[] => {
    if (!allowedCountryCodes) return onboardingCountries;

    const allowed = allowedCountryCodes.map(code => code.toUpperCase());

    return onboardingCountries.filter(country => allowed.includes(country.code));
};

export const getOnboardingCountryName = (countryCode: string): string =>
    getCountryText(countryCode ?? '') || countryCode;

export const formatOnboardingCountry = (country: OnboardingCountry) =>
    `${country.code} — ${country.name}`;

/** Countries where a B2B store can be onboarded. B2C is available in every onboarding country. */
export const b2bCountryCodes = ['SE', 'NO', 'FI', 'DK'];

export const isB2bAvailable = (countryCode: string) =>
    b2bCountryCodes.includes(countryCode?.toUpperCase());

/** Settlement currency per onboarding country: a store settles to a bank account in this currency. */
export const onboardingCountryCurrencies: Record<string, string> = {
    SE: 'SEK',
    FI: 'EUR',
    NO: 'NOK',
    DK: 'DKK',
    DE: 'EUR',
    NL: 'EUR',
    EU: 'EUR',
};

/** The currency a store in the country settles in, or undefined for a country not offered for onboarding. */
export const getOnboardingCountryCurrency = (countryCode: string): string | undefined =>
    onboardingCountryCurrencies[countryCode?.toUpperCase()];
